import axiosInstance from '@/api/axiosInstance';
import { PROFICIENCY_ENDPOINTS, ProficiencyKind } from '@/api/endpoints/proficiency.endpoints';
import {
  Assessor,
  LinkedCourse,
  PositionReadiness,
  ProficiencyRatePayload,
  ProficiencyRating,
  UserProficiencyStatus,
} from '@/types/proficiency.types';

/** Learner skill page → Progress log (GET /skills/{id}/users/{user}/timeline), oldest first. */
export interface SkillTimelineLevel { id: number; label: string; score: number }
export interface SkillTimelineEvent {
  type: 'assigned' | 'unassigned' | 'kept' | 'rating';
  at: string;
  via?: { type: string; name: string | null };
  required?: SkillTimelineLevel | null;
  source?: 'self' | 'manager' | 'sme' | 'course';
  level?: SkillTimelineLevel | null;
  rater?: string | null;
  course?: { id: number; name: string | null } | null;
  comment?: string | null;
  overall_score?: number | null;
  overall?: SkillTimelineLevel | null;
}
export interface SkillTimeline { scale: SkillTimelineLevel[]; required: SkillTimelineLevel | null; events: SkillTimelineEvent[] }

export const ProficiencyApi = {
  getSkillTimeline: async (skillId: number | string, userId: number | string): Promise<SkillTimeline> =>
    (await axiosInstance.get<SkillTimeline>(PROFICIENCY_ENDPOINTS.RATINGS('skill', skillId, userId).replace(/\/ratings$/, '/timeline'))).data,

  getMyStatus: async (kind: ProficiencyKind): Promise<UserProficiencyStatus> =>
    (await axiosInstance.get<UserProficiencyStatus>(PROFICIENCY_ENDPOINTS.MY_STATUS(kind))).data,

  getUserStatus: async (kind: ProficiencyKind, userId: number | string): Promise<UserProficiencyStatus> =>
    (await axiosInstance.get<UserProficiencyStatus>(PROFICIENCY_ENDPOINTS.USER_STATUS(kind, userId))).data,

  rate: async (
    kind: ProficiencyKind,
    entityId: number | string,
    userId: number | string,
    payload: ProficiencyRatePayload,
  ): Promise<ProficiencyRating> =>
    (await axiosInstance.post<ProficiencyRating>(PROFICIENCY_ENDPOINTS.RATINGS(kind, entityId, userId), payload)).data,

  getHistory: async (
    kind: ProficiencyKind,
    entityId: number | string,
    userId: number | string,
  ): Promise<ProficiencyRating[]> =>
    (await axiosInstance.get<ProficiencyRating[]>(PROFICIENCY_ENDPOINTS.RATINGS(kind, entityId, userId))).data,

  // Learner pages: only the linked courses the viewer can take (enrolled, or open to self-enrol and visible).
  getLinkedCourses: async (kind: ProficiencyKind, entityId: number | string): Promise<LinkedCourse[]> =>
    (await axiosInstance.get<LinkedCourse[]>(PROFICIENCY_ENDPOINTS.LINKED_COURSES(kind, entityId), { params: { mine: true } })).data,

  getAssessors: async (kind: ProficiencyKind, entityId: number | string): Promise<Assessor[]> =>
    (await axiosInstance.get<Assessor[]>(PROFICIENCY_ENDPOINTS.ASSESSORS(kind, entityId))).data,

  setAssessors: async (kind: ProficiencyKind, entityId: number | string, userIds: number[]): Promise<Assessor[]> =>
    (await axiosInstance.put<Assessor[]>(PROFICIENCY_ENDPOINTS.ASSESSORS(kind, entityId), { user_ids: userIds })).data,

  getPositionReadiness: async (
    kind: ProficiencyKind,
    positionId: number | string,
    userId?: number | string,
  ): Promise<PositionReadiness> =>
    (await axiosInstance.get<PositionReadiness>(PROFICIENCY_ENDPOINTS.POSITION_READINESS(kind, positionId), {
      params: userId ? { user_id: userId } : undefined,
    })).data,
};
